import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Output, inject } from "@angular/core";
import {
    FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { TranslateModule } from "@ngx-translate/core";
import { SharedModule } from "src/app/shared.module";
import { DropdownOption } from "src/app/components/ui-components/dropdown/dropdown.model";
import { DropdownComponent } from "../../../components/ui-components/dropdown/dropdown.component";
import { FormContainerComponent } from "../../../components/ui-components/forms/form-container/form-container.component";
import { FormInputComponent } from "../../../components/ui-components/forms/form-input/form-input.component";
import { FormTextareaComponent } from "../../../components/ui-components/forms/form-textarea/form-textarea.component";
import { FormInputType } from "../../../components/ui-components/forms/form-input/form-input.model";
import { FinancialDocumentBase } from "../financial-component-base";
import { DenominationItem, FinancialTransactionType, TransactionTypeCodes } from "src/app/services/model/create-financial-transaction.model";
import { TenderType } from "src/app/services/model/tender-type.enum";
import { IGetPaymentTypesResultData } from "src/app/services/model/pos.api.model";
import { PaymentTypesService } from "src/app/services/payment-types.service";

@Component({
  selector: "app-deposit",
  templateUrl: "./deposit.component.html",
  styleUrls: ["./deposit.component.scss"],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DropdownComponent,
    FormContainerComponent,
    FormInputComponent,
    FormTextareaComponent,
        FontAwesomeModule,
        SharedModule,
    TranslateModule,
  ],
  standalone: true,
})
export class DepositComponent extends FinancialDocumentBase {
    FormInputType = FormInputType;
    finDocType = FinancialTransactionType.DepositToTill;
        valuableTypeOptions: DropdownOption[] = [];
    allPaymentTypes: IGetPaymentTypesResultData[] = [];
        selectedPaymentType: IGetPaymentTypesResultData | null = null;
        nominalOptions: DropdownOption[] = [];
    TenderType = TenderType;
        private paymentTypesService = inject(PaymentTypesService);

    @Output() onClose = new EventEmitter<void>();

    form = new FormGroup({
        valuableType: new FormControl<string | null>(null, [Validators.required]),
        value: new FormControl<number | null>(null, [Validators.required]),
        reason: new FormControl<string>("", [Validators.required]),
        note: new FormControl<string>("", [Validators.required]),
        denominationRowsGroup: new FormGroup({
            rows: new FormArray([], Validators.nullValidator),
        }),
    });

    get rows(): FormArray {
        return this.form.controls.denominationRowsGroup.controls.rows as FormArray;
    }

    get hasNominals(): boolean {
        return this.selectedPaymentType?.TenderType !== TenderType.Cash &&
            !!this.selectedPaymentType?.Nominals?.length;
    }

    get totalNominalValue(): number {
        return this.rows.controls.reduce((total, row) => {
            const value = Number(row.get('value')?.value);
            const numberOfItems = Number(row.get('numberOfItems')?.value);
            if (!Number.isFinite(value) || !Number.isFinite(numberOfItems) || value <= 0 || numberOfItems <= 0) {
                return total;
            }

            return total + value * numberOfItems;
        }, 0);
    }

    get isSubmitDisabled(): boolean {
        if (!this.hasNominals) {
            return this.form.invalid;
        }

        return this.form.controls.valuableType.invalid ||
            this.form.controls.reason.invalid ||
            this.form.controls.note.invalid ||
            this.rows.length === 0 ||
            this.rows.invalid ||
            this.totalNominalValue <= 0;
    }


    async ngOnInit() {
        await this.loadData();
        
        // Set first reason option as default if not already set
        if (this.reasonOptions.length > 0 && !this.form.controls.reason.value) {
            this.form.controls.reason.setValue(this.reasonOptions[0].value, { emitEvent: false });
        }
        
        const paymentTypes = await this.paymentTypesService.getPaymentTypesWithNominals();
        this.allPaymentTypes = paymentTypes.filter(pt => pt.TenderType === TenderType.Cash);
        this.valuableTypeOptions = this.allPaymentTypes.map(pt => ({ 
            value: pt.Name, // Use Name as unique identifier
            label: pt.Description 
        }));

        this.form.controls.valuableType.valueChanges.subscribe((paymentName) => {
            this.onPaymentTypeChange(paymentName);
        });

        this.form.controls.denominationRowsGroup.valueChanges.subscribe(() => {
            if (this.hasNominals) {
                this.form.controls.value.setValue(this.totalNominalValue, { emitEvent: false });
            }
        });

        if (this.valuableTypeOptions.length === 1) {
            this.form.controls.valuableType.setValue(this.valuableTypeOptions[0].value);
        }
    }

    private onPaymentTypeChange(paymentName: string | null): void {
        this.selectedPaymentType = this.allPaymentTypes.find(pt => pt.Name === paymentName) || null;
        this.nominalOptions = (this.selectedPaymentType?.Nominals || []).map(n => ({
            value: n.value,
            label: n.denomination,
        }));

        while (this.rows.length > 0) {
            this.rows.removeAt(0);
        }

        if (this.hasNominals) {
            this.form.controls.value.setValue(0, { emitEvent: false });
            this.form.controls.value.disable({ emitEvent: false });
            this.form.controls.value.clearValidators();
            this.addRow();
        } else {
            this.form.controls.value.enable({ emitEvent: false });
            this.form.controls.value.setValidators([Validators.required]);
            this.form.controls.value.setValue(null, { emitEvent: false });
        }

        this.form.controls.value.updateValueAndValidity({ emitEvent: false });
    }

    addRow(): void {
        this.rows.push(new FormGroup({
            numberOfItems: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
            value: new FormControl<number | null>(null, [Validators.required]),
        }));

        const scrollContainer = document.getElementById('depositDenominationRows');
        if (scrollContainer) {
            setTimeout(() => scrollContainer.scrollTo(0, scrollContainer.scrollHeight), 0);
        }
    }

    removeRow(index: number): void {
        if (this.rows.length <= 1) {
            return;
        }

        this.rows.removeAt(index);
        this.form.controls.value.setValue(this.totalNominalValue, { emitEvent: false });
    }

    async createFinancialTransaction() {
        if (this.isSubmitDisabled) {
            this.form.markAllAsTouched();
            return;
        }

        const selectedPaymentName = this.form.controls.valuableType.value;
        const note = this.form.controls.note.value;
        
        // Get currency code from selected payment type using Name as key
        const selectedPaymentType = this.allPaymentTypes.find(pt => pt.Name === selectedPaymentName);
        const currencyCode = selectedPaymentType?.Currency || this.loginService.user.CurrencyCode;

        // Validate selected payment type
        if (!selectedPaymentType) {
            console.error(`Selected payment type not found: ${selectedPaymentName}`);
            return;
        }
        
        // Build attributes with additional data
        const attributes: { [key: string]: string } = {
            'PaymentType': selectedPaymentName || '',
            'TenderType': selectedPaymentType.TenderType.toString() || '',
            'Note': note || ''
        };

        const denominationItems: DenominationItem[] = this.hasNominals
            ? this.rows.controls
                .map((row, index) => {
                    const value = Number(row.get('value')?.value);
                    const quantity = Number(row.get('numberOfItems')?.value);

                    if (!Number.isFinite(value) || !Number.isFinite(quantity) || value <= 0 || quantity <= 0) {
                        return null;
                    }

                    const nominal = selectedPaymentType.Nominals?.find(n => Number(n.value) === value);

                    return {
                        ItemIndex: index,
                        Caption: nominal?.denomination || value.toString(),
                        Value: value,
                        Quantity: quantity,
                        CurrencyISOCode: currencyCode,
                    } as DenominationItem;
                })
                .filter((item): item is DenominationItem => !!item)
            : [];

        const totalPrice = this.hasNominals ? this.totalNominalValue : this.form.controls.value.value;

        await super.createFinancialTransaction(
            TransactionTypeCodes.DepositToTill, // transactionTypeCode
            selectedPaymentType.TenderType, // tenderType
            selectedPaymentType.TenderSubType, // tenderSubType
            totalPrice, // totalPrice
            this.form.controls.reason.value || 'DEPOSIT', // fixedReason
            attributes, // attributes
            currencyCode, // currencyCode from payment type
            '',
            denominationItems,
        );
    }
}
